import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useOffsetPagination } from "apps/web/src/utils/pagination";
import { BulkAssignGoalTemplateJobCreatedByFragment, useBulkAssignGoalTemplateJobOffsetConnectionQuery } from "apps/web/src/types";
import { BulkAssignGoalTemplateStatusHeader } from "./TableHeader";
import { JobStatusModal } from "./JobStatusModal";
import { TableHeaderRow } from "./TableHeaderRow";
import { TableBodyRows } from "./TableBodyRows";
import { NoJobsFound } from "./NoJobsFound";
import TablePage from "apps/web/src/components/universal/TablePage";

export type SelectedJob = {
  id: string;
  name: string;
  dateAssignment: string;
};

type BulkAssignGoalTemplateStatusProps = {
  tabs: string[];
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
};

export const BulkAssignGoalTemplateStatus = ({
  tabs,
  selectedTab,
  setSelectedTab,
}: BulkAssignGoalTemplateStatusProps) => {
  const [selectedJob, setSelectedJob] = useState<SelectedJob>();
  const pagination = useOffsetPagination(15);
  const { offset, limit, query: debouncedSearchText, queryText, onChangeQueryText } = pagination;

  const [selectedCreatedByUsers, setSelectedCreatedByUsers] = useState<BulkAssignGoalTemplateJobCreatedByFragment[]>([]);

  const createdByIds = selectedCreatedByUsers.length === 0 ? null : selectedCreatedByUsers.map(u => u.id);
  const { data, loading, refetch } = useBulkAssignGoalTemplateJobOffsetConnectionQuery({
    variables: {
      input: {
        pagination: {
          limit,
          offset,
        },
        searchTextGoalConfigurationTemplateName: debouncedSearchText ?? null,
        filterByStatus: null,
        createdByIds,
      },
    },
  });

  useEffect(() => {
    refetch();
  }, [pagination.offset]);

  const tableData = data?.bulkAssignGoalTemplateJobOffsetConnection.edges || [];
  const noJobsExist = !tableData.length && !selectedCreatedByUsers.length && debouncedSearchText === "" && !loading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        flex: 1,
        gap: 2,
      }}
    >
      <TablePage
        header={
          <BulkAssignGoalTemplateStatusHeader
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            hasData={!noJobsExist}
            queryText={queryText}
            setQueryText={onChangeQueryText}
            selectedCreatedByUsers={selectedCreatedByUsers}
            setSelectedCreatedByUsers={setSelectedCreatedByUsers}
            refetchBulkAssignmentStatus={refetch}
          />
        }
        tableHeaderRow={!noJobsExist && <TableHeaderRow />}
        paginationHooks={pagination}
        tableBodyRows={
          noJobsExist ? <NoJobsFound /> : tableData.map(job => (
            <TableBodyRows key={job.id} job={job} setSelectedJob={setSelectedJob} />
          ))
        }
        loading={loading}
        total={data?.bulkAssignGoalTemplateJobOffsetConnection.pageInfo.total || 0}
      />
      {!noJobsExist && selectedJob && (
        <JobStatusModal
          open={!!selectedJob}
          onClose={() => setSelectedJob(undefined)}
          selectedJob={selectedJob} />
      )}
    </Box>
  );
};
